import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
} from 'lucide-react';
import { GET_PROJECTS_PAGINATED } from '../../graphql/queries';
import { ProjectCard, ProjectCardData } from './ProjectCard';
import { ProjectsTable } from './ProjectsTable';
import { ProjectDialog } from './ProjectDialog';
import {
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_OPTIONS,
  ProjectStatus,
} from './projectUtils';
import { usePermission } from '../../hooks/usePermission';

const PAGE_SIZE = 12;
const VIEW_MODE_STORAGE_KEY = 'projects.viewMode';
const SORT_STORAGE_KEY = 'projects.sort';
type ViewMode = 'grid' | 'table';
type SortOrder = 'asc' | 'desc';

const ALLOWED_SORT_FIELDS = new Set([
  'name',
  'status',
  'startDate',
  'plannedEndDate',
  'createdAt',
  'updatedAt',
]);

function readInitialViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'grid';
  const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  return stored === 'table' ? 'table' : 'grid';
}

function readInitialSort(): { sortBy: string; sortOrder: SortOrder } {
  const fallback = { sortBy: 'updatedAt', sortOrder: 'desc' as SortOrder };
  if (typeof window === 'undefined') return fallback;
  const raw = window.localStorage.getItem(SORT_STORAGE_KEY);
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as { sortBy?: string; sortOrder?: SortOrder };
    if (parsed.sortBy && ALLOWED_SORT_FIELDS.has(parsed.sortBy)) {
      return {
        sortBy: parsed.sortBy,
        sortOrder: parsed.sortOrder === 'asc' ? 'asc' : 'desc',
      };
    }
  } catch {
    // ignore malformed JSON
  }
  return fallback;
}

interface ProjectsListProps {
  currentUserId?: string;
}

export const ProjectsList: React.FC<ProjectsListProps> = ({ currentUserId }) => {
  const canCreate = usePermission('projects.create');

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<ProjectStatus[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(readInitialViewMode);
  const initialSort = readInitialSort();
  const [sortBy, setSortBy] = useState<string>(initialSort.sortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSort.sortOrder);

  useEffect(() => {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    window.localStorage.setItem(
      SORT_STORAGE_KEY,
      JSON.stringify({ sortBy, sortOrder }),
    );
  }, [sortBy, sortOrder]);

  // Debounce search 300ms
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [selectedStatuses, includeArchived, sortBy, sortOrder]);

  const variables = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      sortBy,
      sortOrder,
      search: debouncedSearch || null,
      statusFilter: selectedStatuses.length > 0 ? selectedStatuses : null,
      includeArchived,
    }),
    [page, sortBy, sortOrder, debouncedSearch, selectedStatuses, includeArchived],
  );

  const handleSortChange = (nextSortBy: string, nextSortOrder: 'asc' | 'desc') => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
  };

  const { data, loading, error } = useQuery<{
    projectsPaginated: {
      items: ProjectCardData[];
      total: number;
      page: number;
      pageSize: number;
    };
  }>(GET_PROJECTS_PAGINATED, {
    variables,
    fetchPolicy: 'cache-and-network',
  });

  const items = data?.projectsPaginated?.items || [];
  const total = data?.projectsPaginated?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggleStatus = (status: ProjectStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-foreground">Projekty</h2>
          <p className="text-muted-foreground mt-2">
            Zarządzaj projektami i tablicami Kanban
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border rounded-md p-0.5">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="text-xs h-7 px-3"
              title="Widok kafelków"
            >
              <LayoutGrid className="w-4 h-4 mr-1" />
              Kafelki
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="text-xs h-7 px-3"
              title="Widok tabelaryczny"
            >
              <List className="w-4 h-4 mr-1" />
              Tabela
            </Button>
          </div>
          {canCreate && (
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Nowy projekt
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wide text-muted-foreground mr-1">
              Status:
            </span>
            {PROJECT_STATUS_OPTIONS.filter((s) => s !== 'ARCHIVED').map((s) => {
              const active = selectedStatuses.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-foreground border-input hover:bg-accent/30'
                  }`}
                >
                  {PROJECT_STATUS_LABELS[s]}
                </button>
              );
            })}
            <label className="flex items-center gap-2 text-sm ml-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="w-4 h-4"
              />
              Pokaż archiwum
            </label>
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <Search className="w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Szukaj projektów..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full md:w-64"
            />
          </div>
        </CardContent>
      </Card>

      {/* Grid */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-destructive">Błąd: {error.message}</p>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <p className="text-lg mb-2">Brak projektów do wyświetlenia</p>
            {canCreate && (
              <Button
                variant="outline"
                className="mt-2"
                onClick={() => setDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Utwórz pierwszy projekt
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      ) : (
        <ProjectsTable
          items={items}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          currentUserId={currentUserId}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Strona {page} z {totalPages} • {total}{' '}
            {total === 1 ? 'projekt' : 'projektów'}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="w-4 h-4" />
              Poprzednia
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Następna
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <ProjectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
};
